import {
  Card,
  ChannelIcon,
  Flex,
  IconButton,
  PencilIcon,
  Skeleton,
  TrashIcon,
  Tooltip,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { useVisibleElements } from '~publish/hooks/useVisibleElements'

import type {
  ChannelGroup_Channel,
  ChannelGroup_ChannelGroup,
} from '../hooks/useChannelGroups'

import styles from './ChannelGroupItem.module.css'

import type { JSX } from 'react'

const ChannelList = ({
  channels,
}: {
  channels: ChannelGroup_Channel[]
}): JSX.Element => {
  const { containerRef, visibleCount, hiddenCount } = useVisibleElements({
    totalCount: channels.length,
    minimumVisibleCount: 1,
  })

  const displayCount =
    hiddenCount > 0 ? Math.max(visibleCount, 1) : visibleCount
  const visibleChannels = channels.slice(0, displayCount)
  const remainingCount = Math.max(0, channels.length - visibleChannels.length)

  return (
    <Flex
      className={styles.channelListRow}
      fullWidth
      align="center"
      gap="xs"
      // Flex renders the layout while the role communicates this dynamic channel collection.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="list"
      aria-label="Channels belonging to this group"
    >
      <Flex ref={containerRef} className={styles.channelList}>
        {visibleChannels.map((channel) => (
          <span
            key={channel.id}
            // Channel cards are rendered as spans to preserve their compact inline layout.
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="listitem"
            className={styles.channelCardItem}
          >
            <ChannelIcon type={channel.service} color="branded" />
            <Text color="subtle" truncate className={styles.channelName}>
              {channel.name}
            </Text>
          </span>
        ))}
      </Flex>
      {remainingCount > 0 && (
        <span
          // The compact counter is part of the same channel collection.
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="listitem"
          className={styles.remainingCount}
          aria-label={`${remainingCount} more channels hidden`}
        >
          +{remainingCount}
        </span>
      )}
    </Flex>
  )
}

const GroupDetails = ({
  channelGroup,
}: {
  channelGroup: ChannelGroup_ChannelGroup
}): JSX.Element => {
  return (
    <Flex
      as="article"
      direction="column"
      gap="2xs"
      fullWidth
      className={styles.groupDetails}
    >
      <Flex gap="2xs" align="center">
        <Text size="md" weight="bold" as="h2" className={styles.groupName}>
          {channelGroup.name}
        </Text>
      </Flex>
      <Skeleton show={!channelGroup.channels}>
        {channelGroup.channels && channelGroup.channels.length > 0 && (
          <ChannelList channels={channelGroup.channels} />
        )}
        {channelGroup.channels && channelGroup.channels.length === 0 && (
          <Text
            color="subtle"
            size="sm"
            icon={<WarningIcon size="small" color="subtle" />}
          >
            No channels in this group. This group will not be available in the
            composer.
          </Text>
        )}
      </Skeleton>
    </Flex>
  )
}

const GroupActions = ({
  channelGroup,
  onEdit,
  onDelete,
}: {
  channelGroup: ChannelGroup_ChannelGroup
  onEdit: (channelGroup: ChannelGroup_ChannelGroup) => void
  onDelete: (channelGroup: ChannelGroup_ChannelGroup) => void
}): JSX.Element => {
  return (
    <Flex gap="2xs" className={styles.groupActions}>
      <Tooltip content="Edit channel group">
        <IconButton
          label={`Edit ${channelGroup.name}`}
          variant="tertiary"
          onClick={(): void => onEdit(channelGroup)}
          className={styles.groupActionsButton}
        >
          <PencilIcon />
        </IconButton>
      </Tooltip>
      <Tooltip content="Delete channel group">
        <IconButton
          label={`Delete ${channelGroup.name}`}
          variant="critical-subtle"
          onClick={(): void => onDelete(channelGroup)}
          className={styles.groupActionsButton}
        >
          <TrashIcon />
        </IconButton>
      </Tooltip>
    </Flex>
  )
}

const ChannelGroupItem = ({
  channelGroup,
  onEdit,
  onDelete,
}: {
  channelGroup: ChannelGroup_ChannelGroup
  onEdit: (channelGroup: ChannelGroup_ChannelGroup) => void
  onDelete: (channelGroup: ChannelGroup_ChannelGroup) => void
}): JSX.Element => {
  return (
    <Card className={styles.channelGroupItem}>
      <GroupDetails channelGroup={channelGroup} />
      <GroupActions
        channelGroup={channelGroup}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </Card>
  )
}

export default ChannelGroupItem
